// A KEZDŐLAP ELRENDEZÉSE — a mentett lista modellje, a feloldás megjelenítésre (`resolveLayout`), a mentéskori
// rejtett-visszafűzés (`mergeHidden`) és a katalógus-szerinti validáció (`dashboardLayoutIssues`). A katalógus a
// `dashboard-tiles.ts`-ben él; a web és a BFF ugyanezeket a tiszta függvényeket használja.
import { DASHBOARD_CATALOG_REV, DASHBOARD_MAX_TILES, DASHBOARD_TILES, dashboardTileDef, tileAllowed, } from './dashboard-tiles.js';
/** Az alapértelmezett elrendezés: a `defaultOrder != null` csempék sorrendben, jog szerint szűrve. */
export function defaultLayout(can) {
    return DASHBOARD_TILES.filter((d) => d.defaultOrder !== null && tileAllowed(d, can))
        .sort((a, b) => (a.defaultOrder ?? 0) - (b.defaultOrder ?? 0))
        .map((def) => ({ def, size: def.defaultSize }));
}
/** A hiányzó KÖTELEZŐ (nem levehető) csempék beszúrása az alap-helyükre (`defaultOrder`), jog szerint. */
function withMandatory(tiles, idOf, can, make) {
    const result = [...tiles];
    for (const def of DASHBOARD_TILES) {
        if (def.removable || def.defaultOrder === null || !tileAllowed(def, can))
            continue;
        if (result.some((t) => idOf(t) === def.id))
            continue;
        const at = result.findIndex((t) => {
            const other = dashboardTileDef(idOf(t));
            return (other?.defaultOrder ?? Infinity) > (def.defaultOrder ?? 0);
        });
        result.splice(at === -1 ? result.length : at, 0, make(def));
    }
    return result;
}
/**
 * A mentett elrendezés feloldása megjelenítésre.
 * - nincs mentett → `defaultLayout` (az új alap-csempék maguktól megjelennek),
 * - van → a lista sorrendje érvényes; a hiányzó KÖTELEZŐ csempe az alap-helyére kerül; az ismeretlen id, a duplikátum és a jog nélküli id kimarad A MEGJELENÍTÉSBŐL
 *   (a jog nélküli a MENTETT listában megmarad — `mergeHidden`); az érvénytelen méret a `defaultSize`-ra esik.
 */
export function resolveLayout(saved, can) {
    if (!saved) {
        return { tiles: defaultLayout(can), source: 'default', catalogRev: DASHBOARD_CATALOG_REV };
    }
    const seen = new Set();
    const tiles = [];
    for (const entry of saved.tiles) {
        const def = dashboardTileDef(entry.id);
        if (!def || seen.has(def.id) || !tileAllowed(def, can))
            continue;
        seen.add(def.id);
        const size = def.allowedSizes.includes(entry.size) ? entry.size : def.defaultSize;
        tiles.push(entry.opts ? { def, size, opts: entry.opts } : { def, size });
    }
    const complete = withMandatory(tiles, (t) => t.def.id, can, (def) => ({ def, size: def.defaultSize }));
    return { tiles: complete, source: 'saved', catalogRev: saved.catalogRev };
}
/** „Új" jelvény a galériában: a csempe a mentett revízió ÓTA jelent meg. */
export function isNewTile(def, catalogRev) {
    return def.since > catalogRev;
}
/** A galéria tartalma: a jogosult, még nem kirakott ÉS levehető csempék (a kötelező csempe sosem a galériában van). */
export function availableTiles(placedIds, can) {
    const placed = new Set(placedIds);
    return DASHBOARD_TILES.filter((d) => d.removable && !placed.has(d.id) && tileAllowed(d, can));
}
/**
 * Mentéskor a szerkesztő csak a LÁTHATÓ csempéket ismeri: a jog nélküli, de ismert id-ket a mentett listából
 * visszafűzi a változatlan RELATÍV helyükre (annak a látható elemnek a mögé, amelyik a mentett listában
 * előttük állt — az elem mozgatásakor követik). Az ismeretlen (kivezetett) id-ket eldobja.
 */
export function mergeHidden(saved, edited, can) {
    const entryFor = (def) => ({
        id: def.id,
        size: def.defaultSize,
    });
    if (!saved)
        return withMandatory([...edited], (e) => e.id, can, entryFor);
    const editedIds = new Set(edited.map((e) => e.id));
    const leading = [];
    const after = new Map();
    let anchor = null;
    for (const entry of saved.tiles) {
        const def = dashboardTileDef(entry.id);
        if (!def)
            continue;
        if (editedIds.has(entry.id)) {
            anchor = entry.id;
            continue;
        }
        if (tileAllowed(def, can))
            continue; // jogosult, de a szerkesztő levette → szándékos eltávolítás
        const bucket = anchor === null ? leading : (after.get(anchor) ?? []);
        bucket.push(entry);
        if (anchor !== null)
            after.set(anchor, bucket);
    }
    const merged = [...leading, ...edited.flatMap((e) => [e, ...(after.get(e.id) ?? [])])];
    // a KÖTELEZŐ csempe nem vehető le: ha a szerkesztő mégis kihagyta, az alap-helyére kerül vissza
    return withMandatory(merged, (e) => e.id, can, entryFor);
}
/**
 * Az elrendezés KATALÓGUS-szerinti ellenőrzése: ismert id, nincs duplikátum, engedett méret, a `catalogRev` a
 * [0, DASHBOARD_CATALOG_REV] tartományban. A JOGOT szándékosan NEM nézi (egy jog nélküli id tárolása ártalmatlan —
 * az adatot a csempe végpontja úgyis kapuzza).
 */
export function dashboardLayoutIssues(layout) {
    const issues = [];
    if (layout.catalogRev < 0 || layout.catalogRev > DASHBOARD_CATALOG_REV) {
        issues.push({ code: 'catalog_rev_out_of_range', index: null });
    }
    if (layout.tiles.length > DASHBOARD_MAX_TILES)
        issues.push({ code: 'too_many_tiles', index: null });
    const seen = new Set();
    layout.tiles.forEach((entry, index) => {
        const def = dashboardTileDef(entry.id);
        if (!def)
            return void issues.push({ code: 'unknown_tile', index });
        if (seen.has(entry.id))
            issues.push({ code: 'duplicate_tile', index });
        seen.add(entry.id);
        if (!def.allowedSizes.includes(entry.size))
            issues.push({ code: 'size_not_allowed', index });
        if (entry.opts?.windowDays !== undefined && def.id !== 'celebrations') {
            issues.push({ code: 'opts_not_allowed', index });
        }
    });
    return issues;
}
//# sourceMappingURL=dashboard-layout.js.map